import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import { AgentTitle, VideoCallHistoryTitle } from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl, mediaUrl } from "../../../Utils/FileUpload";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
interface VideoCallHistory {
  id: number;
  call_date: string; // format: YYYY-MM-DD
  start_time: string; // format: HH:mm:ss
  end_time: string; // format: HH:mm:ss
  customer_mobile: string;
  duration: number; // Duration in minutes
  agent_name: string;
  customer_id: string;
  agent_id: string;
  agent_amount: string;
  file: string;
}
export default function VideoCallHistory() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<VideoCallHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [prevPageBeforeSearch, setPrevPageBeforeSearch] = useState<number>(1);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();
  const location = useLocation();
  const agentId = location?.state?.agent_id || null;
  const customerId = location?.state?.customer_id || null;
  const customerName = location?.state?.customer_name || null;
  const customerMobile = location?.state?.customer_mobile || null;
  // console.log(agentId)
  let personTitle = "";

  if (agentId && filteredItems.length > 0) {
    personTitle = `${filteredItems[0].agent_name || "Agent"}'s Call History`;
  } else if (customerId) {
    const displayName = customerName || customerMobile || "Customer";
    personTitle = `${displayName}'s Call History`;
  }

  const fetchData = async (page = 1, perPage = 10, search = "") => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}video_call_history/getall`, {
        params: {
          page,
          per_page: perPage,
          search: search.trim() !== "" ? search : undefined,
          agent_id: agentId || undefined,
          customer_id: customerId || undefined,
          from_date: fromDate ? fromDate.toISOString().split("T")[0] : null,
          to_date: toDate ? toDate.toISOString().split("T")[0] : null,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      let data: VideoCallHistory[] = response.data.data;
      //  console.log(data,"data");
      // If agentId exists, filter by agent.id
      if (agentId) {
        data = data.filter((item) => item.agent_id === agentId);
        // console.log(data, "data");
      }
      // If customerId exists, filter by customer.id
      if (customerId) {
        data = data.filter((item) => item.customer_id === customerId);
      }
      console.log(data, "data");
      setFilteredItems(data);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.current_page);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterText.trim() === "") {
      fetchData(prevPageBeforeSearch, perPage); // Go back to original page
      setCurrentPage(prevPageBeforeSearch); // Sync UI page
    }
  }, [filterText]);

  useEffect(() => {
    if (filterText.trim() === "") {
      fetchData(currentPage, perPage);
    }
  }, [currentPage, perPage]);

  ///datereset
  useEffect(() => {
    if (fromDate === null && toDate === null) {
      fetchData(currentPage, perPage);
    }
  }, [fromDate, toDate]);
  const handleDateReset = () => {
    setFromDate(null);
    setToDate(null);
  };
  const handleDateFilterClick = () => {
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("From date cannot be greater than To date.");
      return;
    }
    fetchData(currentPage, perPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, filterText); // pass search even when paginating after search
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, filterText);
  };

  const handleSearchClick = () => {
    setPrevPageBeforeSearch(currentPage); // store current page before search
    setCurrentPage(1); // start search from page 1
    fetchData(1, perPage, filterText);
  };

  const handleReset = () => {
    setFilterText("");
    setCurrentPage(prevPageBeforeSearch); // restore to the previous page
    fetchData(prevPageBeforeSearch, perPage); // fetch without search
  };

  // Define columns with actions dynamically based on agentId and customerId
  const getVideoCallHistoryColumns = () => {
    const columns = [
      {
        name: "S.No",
        cell: (row: VideoCallHistory, index: number) => (
          <>{(currentPage - 1) * perPage + index + 1}</>
        ),
      },
    ];

    if (!customerId) {
      columns.push({
        name: "Customer Mobile",
        cell: (row: VideoCallHistory) => <>{row.customer_mobile || "N/A"}</>,
      });
    }

    if (!agentId) {
      columns.push({
        name: "Agent Name",
        cell: (row: VideoCallHistory) => <>{row.agent_name || "N/A"}</>,
      });
    }

    columns.push(
      {
        name: "Call Date",
        cell: (row: VideoCallHistory) => {
          const startFormatted = moment(row.start_time, "HH:mm:ss").format(
            "hh:mmA"
          );
          const endFormatted = moment(row.end_time, "HH:mm:ss").format(
            "hh:mmA"
          );
          return <>{`${row.call_date} ${startFormatted} - ${endFormatted}`}</>;
        },
      },
      {
        name: "Duration",
        cell: (row: VideoCallHistory) => {
          const start = new Date(`1970-01-01T${row.start_time}`);
          const end = new Date(`1970-01-01T${row.end_time}`);
          const diff = Math.floor((end.getTime() - start.getTime()) / 60000);
          return <>{`${diff} mins`}</>;
        },
      },
      {
        name: "Recording",
        cell: (row: VideoCallHistory) => {
          if (!row.file) return <span>N/A</span>;
          const fileUrl = `${mediaUrl}/${row.file}`;
          const ext = row.file.split(".").pop()?.toLowerCase();
          const mimeType = ext === "mov" ? "video/mp4" : `video/${ext}`;
          return (
            <div style={{ maxWidth: "200px" }}>
              <video
                controls
                style={{ width: "100%", height: "auto" }}
                preload="none" // Changed from metadata to none
                key={`video-${row.id}`} // More unique key
                playsInline // Important for mobile
                onError={(e) => console.error("Video error:", e)}
              >
                <source src={fileUrl} type={mimeType} />
                Your browser does not support videos.
              </video>
            </div>
          );
        },
      }
    );

    // Add agent amount column if agentId is present
    if (agentId) {
      columns.push({
        name: `Agent Amount ${currency ? `(${currency})` : ""}`,
        cell: (row: VideoCallHistory) => <>{row.agent_amount || "N/A"}</>,
      });
    }

    return columns;
  };

  // Show a loading spinner while data is being fetched
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={VideoCallHistoryTitle}
        parent={AgentTitle}
        title={VideoCallHistoryTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            {personTitle && (
              <h5
                style={{
                  marginBottom: "1rem",
                  color: "#308e87",
                  fontWeight: "600",
                }}
              >
                {personTitle}
              </h5>
            )}
            <Row className="align-items-center justify-content-end mb-3">
              <Col xl={4}>
                {/* Conditionally show date filter contents, but keep the column space */}
                {agentId && (
                  <div className="d-flex align-items-center gap-2">
                    <label className="mb-0">From:</label>
                    <div style={{ flex: 1 }}>
                      <DatePicker
                        selected={fromDate}
                        onChange={(date: Date | null) => {
                          setFromDate(date);
                          if (!date && !toDate) handleDateReset();
                        }}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        placeholderText="Start date"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        wrapperClassName="datePicker"
                      />
                    </div>

                    <label className="mb-0">To:</label>
                    <div style={{ flex: 1 }}>
                      <DatePicker
                        selected={toDate}
                        onChange={(date: Date | null) => {
                          setToDate(date);
                          if (!date && !fromDate) handleDateReset();
                        }}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        placeholderText="End date"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </div>
                    <Button
                      color="primary"
                      onClick={handleDateFilterClick}
                      title="Filter"
                    >
                      <i className="fa fa-filter" />
                    </Button>
                    <Button
                      color="danger"
                      onClick={handleDateReset}
                      title="Reset Date Filter"
                    >
                      <i className="fa fa-refresh" />
                    </Button>
                  </div>
                )}
              </Col>

              <Col xl={4}>
                <div className=" d-flex align-items-center">
                  <Input
                    type="text"
                    placeholder="Search"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="me-2"
                  />
                  <Button
                    color="primary"
                    onClick={handleSearchClick}
                    className="me-2"
                    title="Search"
                  >
                    <i className="fa fa-search" />
                  </Button>
                  <Button
                    color="danger"
                    onClick={handleReset}
                    title="Reset Search"
                  >
                    <i className="fa fa-refresh" />
                  </Button>
                </div>
              </Col>
            </Row>

            <div className="table-responsive custom-scrollbar">
              <DataTable
                data={filteredItems}
                // columns={videocallHistoryTableColumns}
                columns={getVideoCallHistoryColumns()}
                highlightOnHover
                striped
                customStyles={customStyles}
                pagination
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                responsive
              />
            </div>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer />
    </>
  );
}
